import { useEffect } from "react";
import { Link } from "react-router-dom";

interface RedFlag {
  number: number;
  title: string;
  description: string;
}

const redFlags: RedFlag[] = [
  {
    number: 1,
    title: "Vague or \"All-Inclusive\" Pricing With No Itemised Breakdown",
    description:
      "Ask for a fully itemised written quote covering implants, crowns, CT scans, X-rays, temporaries, lab fees, anaesthesia, and follow-up appointments before paying anything.",
  },
  {
    number: 2,
    title: "Unnamed or \"Local Brand\" Implant Materials",
    description:
      "Always ask for the exact implant manufacturer and model in writing. Trusted brands include Nobel Biocare, Straumann, Osstem, and MegaGen.",
  },
  {
    number: 3,
    title: "No Written Guarantee — or a Guarantee With No Substance",
    description:
      "A real guarantee should clearly explain what is covered, how long it lasts, what voids it, and how UK patients can claim if complications arise.",
  },
  {
    number: 4,
    title: "Pressure Tactics, Urgency, and \"Limited Time\" Offers",
    description:
      "Be cautious of clinics pushing you to book quickly, offering countdown deals, or repeatedly contacting you after your enquiry.",
  },
  {
    number: 5,
    title: "No Clear Policy for Complications After You Return Home",
    description:
      "Ask exactly who you contact after returning to the UK, whether remedial work is covered, and if there is a UK-based contact or partner clinic.",
  },
  {
    number: 6,
    title: "Unverified Registration and Sterilisation Standards",
    description:
      "Verify the clinic's registration with the national authority and ask directly about sterilisation procedures and autoclave protocols.",
  },
  {
    number: 7,
    title: "Excessive Upfront Deposits and Unclear Payment Terms",
    description:
      "Avoid clinics demanding more than 50% upfront before treatment begins or those unable to clearly explain refund and cancellation policies.",
  },
];

export default function Checklist() {
  useEffect(() => {
    const originalTitle = document.title;
    document.title =
      "The 7 Red Flags Guide – Protect Yourself From Bad Dental Clinics Abroad | Dental Safety Abroad";
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Discover the 7 red flags that could ruin your dental treatment abroad and learn how to protect yourself before booking. Free downloadable guide."
      );
    }
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute("content", originalDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-[#182940] pt-24 md:pt-28">
        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 lg:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <nav
              className="flex items-center justify-center gap-2 text-xs text-white/50 mb-6"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-white/80 transition-colors">
                Home
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-white/70" aria-current="page">
                Red Flags Guide
              </span>
            </nav>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
              The Red Flags Guide
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-2xl mx-auto">
              Before you pay any deposit — read this. The RED FLAG GUIDE separates
              trustworthy clinics from ones to avoid.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {/* Left: Red Flags List */}
            <div className="lg:col-span-2">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-6">
                The 7 Biggest Red Flags to Watch For
              </h2>
              <ul className="space-y-4" aria-label="Red flags guide">
                {redFlags.map((flag) => (
                  <li
                    key={flag.number}
                    className="flex items-start gap-4 bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5"
                  >
                    <span
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-red-500 text-white text-sm font-bold shrink-0 mt-0.5"
                      aria-hidden="true"
                    >
                      {flag.number}
                    </span>
                    <div>
                      <div className="text-xs font-semibold text-red-600 mb-0.5">
                        Red Flag {flag.number}
                      </div>
                      <strong className="text-sm md:text-base text-foreground-900 block mb-1">
                        {flag.title}
                      </strong>
                      <span className="text-sm text-foreground-600 leading-relaxed">
                        {flag.description}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Sidebar Cards */}
            <div className="space-y-5">
              {/* Download Card */}
              <div className="bg-[#182940] rounded-lg p-5 md:p-6 text-white">
                <h3 className="text-base font-semibold text-white mb-2">
                  Get the Full Red Flags Guide
                </h3>
                <p className="text-sm text-white/60 leading-relaxed mb-4">
                  Get a printable version of this guide.
                </p>
                <a
                  href="https://dentalsafetyabroad.gumroad.com/l/uiskv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full px-4 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
                  aria-label="Get the Red Flags Guide on Gumroad"
                >
                  <span className="mr-1.5">📘</span> Access the Free Guide
                </a>
              </div>

              {/* More Guidance Card */}
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-2">
                  Want More Guidance?
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  There are several more comprehensive options available to ensure you
                  have more detailed guidance. View our packages here.
                </p>
                <Link
                  to="/protection-packages"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Your Protection Packages →
                </Link>
              </div>

              {/* How to Use Card */}
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-3">
                  <span className="text-xl">💡</span>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">
                  How to Use This Guide
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Contact each clinic you're considering and work through these
                  questions. Record their answers. A trustworthy clinic will answer
                  every question clearly and willingly — without pressure or
                  evasion.
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                  If a clinic refuses to answer, becomes defensive, or gives vague
                  non-answers — take that as a signal to look elsewhere.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-100/70">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-3">
            Not Sure Which Country to Choose?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            Take our free two-minute quiz and get a personalised destination
            recommendation — from Hungary, Poland, Turkey, and Spain — based on
            your treatment type, budget, and travel preferences.
          </p>
          <Link
            to="/quiz"
            className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Take the Free Quiz →
          </Link>
        </div>
      </section>
    </div>
  );
}